// RFC 4122 v4 UUID built from `crypto.getRandomValues` — the one API
// every runtime this package reaches actually has: Node (apps/api), the
// browser (apps/web-rnw), and RN/Hermes, where it arrives through the
// `react-native-get-random-values` polyfill. ⛔ Not `crypto.randomUUID`:
// Hermes 0.85 has none natively, and the polyfill does NOT add it
// (verified against v1.11.0 source — it ships getRandomValues only).
//
// Lives in @heraion/shared because `blankSlot` mints slot ids here
// (PE-47) and `shared` cannot import from `ui`; @heraion/ui/uuid is a
// re-export, which is what mobile's 10 callsites still import.
//
// Why a real UUID and not any unique string: every server schema that
// accepts a client-supplied id validates it through Zod's z.uuid(), so
// a non-UUID gets rejected with `invalid_payload`. The session id goes
// into POST /sessions, the LogSet ids into POST /sets/batch.
//
// The Math.random fallback below is a degraded LAST RESORT, not a
// promise it never runs: on mobile it would mean the polyfill failed to
// install (index.js imports it before anything else), and on a server
// or browser it should be unreachable. It still returns a well-formed
// UUID so the parse boundary stays happy — it is simply not
// cryptographically random, which matters wherever the id is a secret.
//
// Ported from the standard reference algorithm. Per byte:
//   bytes[6] high nibble = 0100 (version 4)
//   bytes[8] high two bits = 10   (variant 1, RFC 4122)

const HEX = "0123456789abcdef";

function toHex(b: number): string {
  return HEX[(b >> 4) & 0x0f]! + HEX[b & 0x0f]!;
}

export function uuidv4(): string {
  const c = (
    globalThis as unknown as {
      crypto?: { getRandomValues?: <T extends ArrayBufferView>(arr: T) => T };
    }
  ).crypto;
  if (!c?.getRandomValues) {
    // Crypto polyfill missing entirely — fall back to a degraded UUID
    // built from Math.random. Still a valid UUID format (passes
    // z.uuid()), just not cryptographically random. Should never fire
    // in production: index.js imports react-native-get-random-values
    // before anything else.
    const r = () => Math.floor(Math.random() * 256);
    const bytes = new Uint8Array(16);
    for (let i = 0; i < 16; i++) bytes[i] = r();
    return assembleUuid(bytes);
  }
  const bytes = new Uint8Array(16);
  c.getRandomValues(bytes);
  return assembleUuid(bytes);
}

function assembleUuid(bytes: Uint8Array): string {
  bytes[6] = (bytes[6]! & 0x0f) | 0x40;
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;
  return (
    toHex(bytes[0]!) +
    toHex(bytes[1]!) +
    toHex(bytes[2]!) +
    toHex(bytes[3]!) +
    "-" +
    toHex(bytes[4]!) +
    toHex(bytes[5]!) +
    "-" +
    toHex(bytes[6]!) +
    toHex(bytes[7]!) +
    "-" +
    toHex(bytes[8]!) +
    toHex(bytes[9]!) +
    "-" +
    toHex(bytes[10]!) +
    toHex(bytes[11]!) +
    toHex(bytes[12]!) +
    toHex(bytes[13]!) +
    toHex(bytes[14]!) +
    toHex(bytes[15]!)
  );
}
